import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabaseGet, supabasePost, supabasePatch } from '@/utils/supabaseRest';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { Check, Target, ArrowRight, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { createStripeCustomer } from '@/api/stripe';

// ─── Vertical Detection ──────────────────────────────────────

const VERTICAL_LABELS: Record<string, string> = {
  auto_insurance: 'Auto Insurance',
  home_insurance: 'Home Insurance',
  health_insurance: 'Health Insurance',
  life_insurance: 'Life Insurance',
  debt_relief: 'Debt Relief',
  debt_consolidation: 'Debt Consolidation',
  solar: 'Solar',
  mortgage: 'Mortgage',
  personal_loan: 'Personal Loan',
  medicare: 'Medicare',
  tax_relief: 'Tax Relief',
  home_warranty: 'Home Warranty',
  home_security: 'Home Security',
  legal: 'Legal',
};

const VERTICAL_TEMPLATES: Record<string, { field: string; question: string; options: string[] }> = {
  auto_insurance: { field: 'coverage_type', question: 'What type of auto coverage are you looking for?', options: ['Full Coverage', 'Liability Only', 'Switching Carriers', 'New Policy'] },
  debt_relief: { field: 'debt_amount', question: 'How much debt are you looking to resolve?', options: ['$10K - $25K', '$25K - $50K', '$50K - $100K', 'Over $100K'] },
  solar: { field: 'home_type', question: 'What type of property needs solar?', options: ['Single Family Home', 'Townhouse', 'Multi-Family', 'Commercial'] },
  mortgage: { field: 'loan_type', question: 'What type of mortgage are you looking for?', options: ['Purchase', 'Refinance', 'Cash-Out Refi', 'Home Equity'] },
  health_insurance: { field: 'coverage_need', question: 'What kind of health coverage do you need?', options: ['Individual', 'Family', 'Medicare', 'Short-Term'] },
  life_insurance: { field: 'policy_type', question: 'What type of life insurance are you interested in?', options: ['Term Life', 'Whole Life', 'Universal Life', 'Final Expense'] },
};

/**
 * Detect the user's likely vertical from URL params, UTM campaign, or referrer.
 * Priority: ?vertical= > ?utm_campaign= > document.referrer pattern match.
 */
function detectVerticalFromContext(): string | null {
  const params = new URLSearchParams(window.location.search);

  // 1. Explicit vertical param (highest priority)
  const verticalParam = params.get('vertical');
  if (verticalParam && VERTICAL_LABELS[verticalParam]) {
    return verticalParam;
  }

  // 2. UTM campaign containing a vertical keyword
  const utmCampaign = params.get('utm_campaign') || '';
  if (utmCampaign) {
    for (const key of Object.keys(VERTICAL_LABELS)) {
      if (utmCampaign.toLowerCase().includes(key.replace(/_/g, ''))) return key;
      // Also match partial keywords
      const parts = key.split('_');
      if (parts.some((part) => utmCampaign.toLowerCase().includes(part) && part.length > 3)) {
        return key;
      }
    }
  }

  // 3. Referrer domain patterns
  try {
    const referrer = document.referrer || '';
    if (referrer) {
      const ref = referrer.toLowerCase();
      if (ref.includes('insurance')) return 'auto_insurance';
      if (ref.includes('debt')) return 'debt_relief';
      if (ref.includes('solar')) return 'solar';
      if (ref.includes('mortgage') || ref.includes('refi')) return 'mortgage';
      if (ref.includes('medicare')) return 'medicare';
      if (ref.includes('tax')) return 'tax_relief';
      if (ref.includes('legal') || ref.includes('lawyer') || ref.includes('attorney')) return 'legal';
      if (ref.includes('loan')) return 'personal_loan';
    }
  } catch {
    // Referrer access can throw in certain contexts — non-blocking
  }

  return null;
}

interface Plan {
  id: string;
  name: string;
  description: string | null;
  stripe_price_id: string;
  price: number;
  features: string[];
  max_chatbots: number;
  max_conversations_per_month: number | null;
  is_active: boolean;
  trial_period_days?: number;
}

const PortalSignup: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState<'account' | 'plan'>('account');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });

  const { data: plans } = useQuery({
    queryKey: ["activePlans"],
    queryFn: async () => {
      try {
        const data = await supabaseGet<Plan[]>('plans?select=*&is_active=eq.true&order=price');
        return data;
      } catch (err: any) {
        // Don't throw — plans table may not exist yet (Stripe setup deferred).
        // Return empty array so signup can still proceed with free-tier default.
        console.warn("plans table not available, defaulting to free tier:", err.message);
        return [] as Plan[];
      }
    },
    retry: false,
  });

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (formData.password.length < 6) {
      toast({ title: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }

    // Skip plan selection — auto-assign free plan and create account immediately
    setIsLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: { name: formData.name },
          emailRedirectTo: `${window.location.origin}/portal`
        }
      });
      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create account');

      // Upsert profile with only columns that exist in the current schema.
      // Legacy profiles table has: id, email, full_name, plan, role, stripe_customer_id.
      // Auth user_metadata already stores 'name'; profile.full_name mirrors it for the UI.
      try {
        const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || '';
        const SUPABASE_KEY = import.meta.env?.VITE_SUPABASE_ANON_KEY || '';
        const tokenRaw = localStorage.getItem('supabase.auth.token');
        const accessToken = tokenRaw ? JSON.parse(tokenRaw).access_token : null;
        await fetch(`${SUPABASE_URL}/rest/v1/profiles`, {
          method: 'POST',
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': `Bearer ${accessToken || SUPABASE_KEY}`,
            'Content-Type': 'application/json',
            'Prefer': 'resolution=merge-duplicates',
          },
          body: JSON.stringify({ id: authData.user.id, email: formData.email, full_name: formData.name }),
        });
      } catch (profileErr) {
        console.warn('Profile upsert failed (non-blocking):', profileErr);
      }

      // If email confirmation is disabled in Supabase, session is already set
      // If enabled, sign in immediately
      if (!authData.session) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });
        if (signInError) {
          toast({ title: "Account Created!", description: "Check your email to verify your account." });
          navigate('/portal/auth?message=check-email');
          return;
        }
      }

      // ─── Onboarding: seed a starter chatbot + 4-node flow ───────────────
      // Graceful degradation: any failure falls through to /portal.
      try {
        // Detect vertical from URL params, UTM, or referrer
        const detectedVertical = detectVerticalFromContext();
        const verticalLabel = detectedVertical ? VERTICAL_LABELS[detectedVertical] : null;
        const verticalTemplate = detectedVertical ? VERTICAL_TEMPLATES[detectedVertical] : null;

        // 1. Create the starter chatbot — customize description if vertical detected
        const chatbotDescription = verticalLabel
          ? `Optimized for ${verticalLabel}`
          : 'Your starter lead-collection flow. Edit each step to match your business.';
        const chatbotRows = await supabasePost<any[]>('chatbots', {
          user_id: authData.user.id,
          name: verticalLabel ? `${verticalLabel} Flow` : 'My First Chatbot',
          description: chatbotDescription,
        });
        const newChatbot = chatbotRows[0];
        if (!newChatbot) throw new Error('Chatbot creation returned no data');

        // 2. Pre-generate node UUIDs so we can wire `next` + `start_node` before insert
        const welcomeId = crypto.randomUUID();
        const emailId = crypto.randomUUID();
        const interestId = crypto.randomUUID();
        const endId = crypto.randomUUID();

        // Build the interest/vertical question — use template if vertical detected
        const interestQuestion = verticalTemplate
          ? verticalTemplate.question
          : 'What brings you here today?';
        const interestOptions = verticalTemplate
          ? verticalTemplate.options
          : ['Just browsing', 'Ready to buy', 'Need more info'];
        const interestField = verticalTemplate
          ? verticalTemplate.field
          : 'interest';

        // 3. Insert nodes FIRST (flow_definitions.start_node FK requires them to exist)
        await supabasePost('flow_nodes', [
          {
            id: welcomeId,
            chatbot_id: newChatbot.id,
            type: 'question',
            field: 'intro',
            question: verticalLabel
              ? `Hi! I'll help you find the best ${verticalLabel.toLowerCase()} options. What's your first name?`
              : "Hi! I'm your new AI assistant. I'll help collect leads for you. What's your first name?",
            input_type: 'text',
            position: 0,
            next: [emailId],
          },
          {
            id: emailId,
            chatbot_id: newChatbot.id,
            type: 'question',
            field: 'email',
            question: 'Nice to meet you! What\'s the best email to reach you at?',
            input_type: 'email',
            position: 1,
            validation: {
              required: true,
              pattern: '^[^@]+@[^@]+\\.[^@]+$',
              errorMessage: 'Please enter a valid email',
            },
            next: [interestId],
          },
          {
            id: interestId,
            chatbot_id: newChatbot.id,
            type: 'question',
            field: interestField,
            question: interestQuestion,
            input_type: 'select',
            options: interestOptions,
            position: 2,
            next: [endId],
          },
          {
            id: endId,
            chatbot_id: newChatbot.id,
            type: 'end',
            field: 'end',
            question: "Thanks! We'll be in touch soon. 🎯",
            position: 3,
            next: [],
          },
        ]);

        // 4. Insert the flow definition pointing at the welcome node
        await supabasePost('flow_definitions', {
          chatbot_id: newChatbot.id,
          name: 'Starter Flow',
          start_node: welcomeId,
          is_active: true,
          meta: {},
        });

        toast({
          title: 'Welcome to IntentDrive.ai!',
          description: 'Your first flow is ready to customize.',
        });
        navigate(`/portal/flow/${newChatbot.id}`);
        return;
      } catch (onboardingError: any) {
        console.error('Onboarding seed failed (non-blocking):', onboardingError);
        toast({
          title: 'Account created!',
          description: "Let's get you started...",
        });
        navigate('/portal');
        return;
      }
    } catch (error: any) {
      toast({ title: 'Signup Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelection = async (planId: string) => {
    setSelectedPlanId(planId);
    setIsLoading(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: { name: formData.name },
          emailRedirectTo: `${window.location.origin}/portal/auth?verified=true`
        }
      });
      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create account');

      const selectedPlan = plans?.find(p => p.id === planId);
      await supabasePatch(`profiles?id=eq.${authData.user.id}`, {
        name: formData.name,
        plan_id: planId || null,
        subscription_status: selectedPlan && selectedPlan.price > 0 ? 'pending' : 'free',
      });

      try {
        await createStripeCustomer(formData.email, formData.name);
      } catch (stripeError) {
        console.warn('Stripe customer creation deferred:', stripeError);
      }

      if (selectedPlan && selectedPlan.price > 0 && selectedPlan.stripe_price_id) {
        try {
          const hasStripeConfig = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
          if (hasStripeConfig) {
            const { initializeStripe } = await import('@/api/stripe');
            const stripe = await initializeStripe();
            if (stripe) {
              const { error } = await stripe.redirectToCheckout({
                lineItems: [{ price: selectedPlan.stripe_price_id, quantity: 1 }],
                mode: 'subscription',
                successUrl: `${window.location.origin}/portal/billing?success=true&plan=${planId}&user=${authData.user.id}`,
                cancelUrl: `${window.location.origin}/portal/billing?canceled=true`,
                customerEmail: authData.user.email,
              });
              if (error) {
                toast({ title: "Checkout Error", description: error.message, variant: "destructive" });
                navigate('/portal/billing?error=stripe');
              }
              return;
            }
          }
          toast({ title: "Account Created!", description: `Selected ${selectedPlan.name}. Billing setup pending.` });
          navigate('/portal/billing?demo=true');
        } catch (error: any) {
          toast({ title: "Checkout Error", description: error.message, variant: "destructive" });
          navigate('/portal/billing?error=checkout');
        }
      } else {
        toast({ title: "Account Created!", description: "Check your email to verify your account." });
        navigate('/portal/auth?message=check-email');
      }
    } catch (error: any) {
      toast({ title: 'Signup Failed', description: error.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-[-20%] right-[10%] w-[600px] h-[600px] bg-emerald-500/[0.05] rounded-full blur-[150px]" />
        <div className="absolute bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-blue-500/[0.04] rounded-full blur-[120px]" />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(148,163,184,0.04) 1px, transparent 0)`,
        backgroundSize: '40px 40px',
      }} />

      {/* Nav */}
      <header className="relative z-10 border-b border-slate-800/50 backdrop-blur-md bg-slate-950/80">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.jpg" alt="IntentDrive.ai" className="h-14 w-auto rounded" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
            <span className="font-bold text-lg">IntentDrive</span>
            <span className="text-[10px] text-emerald-400 font-bold">.ai</span>
          </Link>
          <Link to="/portal/auth" className="text-sm text-slate-400 hover:text-white transition-colors">
            Already have an account? <span className="text-emerald-400">Sign in</span>
          </Link>
        </div>
      </header>

      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-65px)] py-12 px-4">
        {step === 'account' ? (
          /* ═══ STEP 1: Account Form ═══ */
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img src="/logo.jpg" alt="IntentDrive.ai" className="h-20 w-auto rounded-2xl mx-auto mb-4 shadow-lg shadow-emerald-500/20" style={{ filter: 'brightness(1.2) sepia(1) hue-rotate(100deg) saturate(2)' }} />
              <h1 className="text-3xl font-bold mb-2">Create Your Account</h1>
              <p className="text-slate-400">Start building conversion flows in minutes</p>
            </div>

            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 backdrop-blur-xl p-8">
              <form onSubmit={handleAccountSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Full Name</label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 6 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={6}
                      className="bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 h-11 rounded-xl pr-10"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirm Password</label>
                  <Input
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    minLength={6}
                    className="bg-slate-800/50 border-slate-700 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20 h-11 rounded-xl"
                  />
                </div>

                <Button type="submit" disabled={isLoading} className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/20 mt-2 disabled:opacity-50">
                  {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Account...</> : <>Create Free Account <ArrowRight className="ml-2 h-4 w-4" /></>}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-slate-800 text-center">
                <p className="text-xs text-slate-500">
                  By signing up, you agree to our{' '}
                  <Link to="/terms" className="text-emerald-400 hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-emerald-400 hover:underline">Privacy Policy</Link>
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* ═══ STEP 2: Plan Selection ═══ */
          <div className="w-full max-w-5xl">
            <div className="text-center mb-10">
              <h1 className="text-3xl font-bold mb-2">Choose Your Plan</h1>
              <p className="text-slate-400">Select the plan that fits your needs. Upgrade anytime.</p>
            </div>

            <div className="grid gap-6 md:grid-cols-3 mb-10">
              {plans?.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                const isFree = plan.price === 0;
                return (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`relative p-6 rounded-2xl border cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? 'border-emerald-500 bg-emerald-500/5 shadow-lg shadow-emerald-500/10'
                        : 'border-slate-800 bg-slate-900/50 hover:border-slate-700'
                    }`}
                  >
                    {isSelected && (
                      <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                        <Check className="h-3.5 w-3.5 text-white" />
                      </div>
                    )}
                    <h3 className="text-xl font-semibold mb-1">{plan.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold">{isFree ? 'Free' : `$${plan.price}`}</span>
                      {!isFree && <span className="text-slate-500 text-sm">/month</span>}
                      {plan.trial_period_days && plan.trial_period_days > 0 && (
                        <p className="text-xs text-emerald-400 font-medium mt-1">{plan.trial_period_days}-day free trial</p>
                      )}
                    </div>
                    {plan.description && <p className="text-sm text-slate-400 mb-4">{plan.description}</p>}
                    <ul className="space-y-2.5">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <Check className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                          <span className="text-slate-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              {/* Show placeholder cards if no plans loaded */}
              {!plans?.length && [1, 2, 3].map(i => (
                <div key={i} className="p-6 rounded-2xl border border-slate-800 bg-slate-900/50">
                  <div className="h-6 bg-slate-800 rounded w-24 mb-3 animate-pulse" />
                  <div className="h-10 bg-slate-800 rounded w-20 mb-4 animate-pulse" />
                  <div className="space-y-2">
                    {[1, 2, 3].map(j => <div key={j} className="h-4 bg-slate-800 rounded w-full animate-pulse" />)}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-3">
              <Button variant="outline" onClick={() => setStep('account')} disabled={isLoading}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-xl px-6">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button
                onClick={() => selectedPlanId && handlePlanSelection(selectedPlanId)}
                disabled={!selectedPlanId || isLoading}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold rounded-xl px-8 shadow-lg shadow-emerald-500/20 disabled:opacity-50"
              >
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating Account...</> : <>Create Account <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalSignup;
